import React, { useState, useMemo, useEffect, DragEvent } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  FolderOpen, RefreshCw, Search,
  LayoutList, LayoutGrid, ChevronUp,
  Folder, File as FileIcon, Smartphone, Monitor,
  ChevronRight, MoreVertical, Trash2, Download,
  Filter, ListX
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useFileStore, FileEntry } from "@/stores/files.store";
import { useDeviceStore } from "@/stores/device.store";
import { fileSystemService } from "@/services/filesystem.service";
import { webAdbService } from "@/services/webadb.service";
import { useLogStore } from "@/stores/log.store";
import { useFileOperations } from "@/hooks/use-file-operations";
import { useDragDrop } from "@/hooks/use-drag-drop";
import { Virtuoso } from "react-virtuoso";

interface FilePanelProps {
  side: 'left' | 'right'
}

export const FilePanel: React.FC<FilePanelProps> = ({ side }) => {
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [searchQuery, setSearchQuery] = useState("");
  const [presetHint, setPresetHint] = useState("");

  // 获取本地日期字符串 (YYYY-MM-DD)
  const getLocalDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // 动态日期，午夜自动更新
  const [todayDate, setTodayDate] = useState(getLocalDate);

  useEffect(() => {
    const updateDate = () => setTodayDate(getLocalDate());
    // 计算到午夜的毫秒数
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const msToMidnight = midnight.getTime() - now.getTime();

    const timeout = setTimeout(() => {
      updateDate();
      // 之后每24小时更新一次
      const interval = setInterval(updateDate, 24 * 60 * 60 * 1000);
      return () => clearInterval(interval);
    }, msToMidnight);

    return () => clearTimeout(timeout);
  }, []);

  const todayDateNoDash = todayDate.replace(/-/g, '');
  // sensor_tools GPS 文件名格式：2026_03_01_16_07_31_103-location.csv（年_月_日_时_分_秒）
  const todayDateUnderscore = todayDate.replace(/-/g, '_');
  
  const sideState = useFileStore(state => state[side]);
  const { setPath, setFiles, setDirectoryHandle, toggleSelect, selectAll, clearSelection, navigateUpLocal, removeSelectedFromList } = useFileStore();
  const { isConnected, adb, connect } = useDeviceStore();
  const { addLog } = useLogStore();
  const { search: performSearch, refreshSide } = useFileOperations();
  const {
    isDropTarget,
    dropTargetFolder,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFolderDragOver,
    handleFolderDragLeave,
    handleFolderDrop
  } = useDragDrop(side);

  const { path, files, selectedPaths, directoryHandle, isSearching, isRefreshing } = sideState;

  const isLoading = isSearching || isRefreshing;

  const filteredFiles = files; // Server-side (ADB/Local) search result is already filtered when query exists

  const isAdb = path.startsWith('adb://');

  // 计算选中文件总大小
  const selectedTotalSize = useMemo(() => {
    return files
      .filter(f => selectedPaths.includes(f.path) && f.type !== 'directory')
      .reduce((sum, f) => sum + f.size, 0);
  }, [files, selectedPaths]);

  const refresh = () => refreshSide(side);

  const openLocalFolder = async () => {
    try {
      const handle = await fileSystemService.selectDirectory();
      setDirectoryHandle(side, handle, false); // Don't push to stack for new root folder
      setPath(side, handle.name);
      const entries = await fileSystemService.readDirectory(handle);
      setFiles(side, entries);
      addLog('success', `已打开本地文件夹: ${handle.name}`);
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        addLog('error', `打开文件夹失败: ${error.message}`);
      }
    }
  };

  const openDeviceFolder = async () => {
    let currentAdb = adb;
    if (!isConnected || !currentAdb) {
      try {
        await connect();
        currentAdb = useDeviceStore.getState().adb;
      } catch (error: any) {
        return; // Error already logged in store
      }
    }

    if (!currentAdb) return;

    try {
      const storagePath = await webAdbService.findStoragePath(currentAdb);
      setPath(side, `adb://${storagePath}`);
      const deviceFiles = await webAdbService.findFiles(currentAdb, storagePath, ['*']);
      setFiles(side, deviceFiles);
    } catch (error: any) {
      addLog('error', `读取设备目录失败: ${error.message}`);
    }
  };

  const handleDoubleClick = async (file: FileEntry) => {
    if (file.type !== 'directory') return;

    try {
      if (isAdb) {
        if (!adb) return;
        setPath(side, `adb://${file.path}`);
        const newFiles = await webAdbService.findFiles(adb, file.path, ['*']);
        setFiles(side, newFiles);
      } else if (directoryHandle) {
        // For local, we need to get the sub-directory handle and push current to stack
        const subHandle = await directoryHandle.getDirectoryHandle(file.name);
        setDirectoryHandle(side, subHandle, true); // Push current handle to stack
        setPath(side, subHandle.name);
        const newFiles = await fileSystemService.readDirectory(subHandle);
        setFiles(side, newFiles);
      }
    } catch (error: any) {
      addLog('error', `打开目录失败: ${error.message}`);
    }
  };

  const handleNavigateUp = async () => {
    if (!path || path === '/' || path === 'adb:///' || path === 'adb://') return;

    try {
      if (isAdb) {
        if (!adb) return;
        const rawPath = path.replace('adb://', '');
        const parts = rawPath.split('/').filter(p => p !== '');
        parts.pop();
        const newRawPath = parts.length > 0 ? '/' + parts.join('/') : '/';
        setPath(side, `adb://${newRawPath}`);
        const newFiles = await webAdbService.findFiles(adb, newRawPath, ['*']);
        setFiles(side, newFiles);
      } else if (directoryHandle) {
        // Use the stack to navigate up
        const stack = sideState.directoryHandleStack;
        if (stack.length > 0) {
          navigateUpLocal(side);
          // Read files from the parent handle after state update
          const parentHandle = stack[stack.length - 1];
          const newFiles = await fileSystemService.readDirectory(parentHandle);
          setFiles(side, newFiles);
        } else {
          addLog('info', '已到达授权目录的根目录');
        }
      }
    } catch (error: any) {
      addLog('error', `返回上级失败: ${error.message}`);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      selectAll(side);
    } else {
      clearSelection(side);
    }
  };

  const handleSort = (field: any) => {
    useFileStore.getState().setSort(side, field);
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) return "--";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(date);
  };

  const breadcrumbs = useMemo(() => {
    if (!path) return [];
    const isAdbPath = path.startsWith('adb://');

    if (isAdbPath) {
      // ADB path - use the path string
      const rawPath = path.replace('adb://', '');
      const parts = rawPath.split('/').filter(p => p !== '');

      const result = [];
      result.push({ name: '设备根目录', path: 'adb:///' });

      parts.forEach((part, index) => {
        const partPath = '/' + parts.slice(0, index + 1).join('/');
        result.push({
          name: part,
          path: `adb://${partPath}`
        });
      });

      return result;
    } else {
      // Local path - use the directory handle stack
      const stack = sideState.directoryHandleStack;
      const result: { name: string; path: string }[] = [];
      
      // Add all parent directories from stack
      stack.forEach((handle) => {
        result.push({
          name: handle.name,
          path: handle.name
        });
      });
      
      // Add current directory
      if (directoryHandle) {
        result.push({
          name: directoryHandle.name,
          path: directoryHandle.name
        });
      }
      
      return result;
    }
  }, [path, sideState.directoryHandleStack, directoryHandle]);

  const handleBreadcrumbClick = async (targetPath: string) => {
    try {
      if (targetPath.startsWith('adb://')) {
        if (!adb) return;
        const adbPath = targetPath.replace('adb://', '') || '/';
        setPath(side, targetPath);
        const newFiles = await webAdbService.findFiles(adb, adbPath, ['*']);
        setFiles(side, newFiles);
      } else if (directoryHandle) {
        // For local, navigate using the stack
        const currentStack = sideState.directoryHandleStack;
        const targetName = targetPath === '/' ? currentStack[0]?.name : targetPath;
        
        if (targetName === directoryHandle.name) {
          // Already at this level, just refresh
          const newFiles = await fileSystemService.readDirectory(directoryHandle);
          setFiles(side, newFiles);
        } else {
          // Find the target handle in the stack
          const targetIndex = currentStack.findIndex(h => h.name === targetName);
          
          if (targetIndex >= 0) {
            // Navigate to that level
            const targetHandle = currentStack[targetIndex];
            const newStack = currentStack.slice(0, targetIndex);
            
            useFileStore.setState((state) => ({
              [side]: {
                ...state[side],
                directoryHandle: targetHandle,
                directoryHandleStack: newStack,
                path: targetHandle.name
              }
            }));
            
            const newFiles = await fileSystemService.readDirectory(targetHandle);
            setFiles(side, newFiles);
          } else {
            addLog('info', '本地目录层级跳转受限');
          }
        }
      }
    } catch (error: any) {
      addLog('error', `跳转失败: ${error.message}`);
    }
  };

  return (
    <div
      className={cn(
        "h-full flex flex-col bg-background border-r last:border-r-0 transition-colors",
        isDropTarget && "bg-primary/5 ring-2 ring-primary/30 ring-inset",
        isLoading && "pointer-events-none opacity-60"
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* 面板头部 */}
      <div className="h-10 border-b flex items-center justify-between px-3 bg-muted/30">
        <div className="flex items-center gap-1 flex-1 min-w-0 overflow-hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                <FolderOpen className="w-4 h-4 text-primary" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuItem onClick={openLocalFolder} className="gap-2 text-xs">
                <Monitor className="w-3.5 h-3.5" /> 打开本地文件夹
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openDeviceFolder} className="gap-2 text-xs">
                <Smartphone className="w-3.5 h-3.5" /> 打开手机目录
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled className="text-[10px] uppercase font-bold text-muted-foreground">
                {isConnected ? "手机已连接" : "手机未连接"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Breadcrumbs */}
          <div className="flex items-center overflow-x-auto no-scrollbar mask-fade-right py-1">
            {breadcrumbs.length === 0 ? (
              <span className="text-xs text-muted-foreground italic px-1">未选择路径</span>
            ) : (
              breadcrumbs.map((bc, i) => (
                <React.Fragment key={bc.path}>
                  {i > 0 && <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />}
                  <Button
                    variant="ghost"
                    className="h-7 px-1.5 text-xs font-medium hover:bg-muted whitespace-nowrap shrink-0"
                    onClick={() => handleBreadcrumbClick(bc.path)}
                  >
                    {bc.name}
                  </Button>
                </React.Fragment>
              ))
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-1 ml-2">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleNavigateUp} disabled={!path || path === '/' || path === 'adb:///' || isLoading}>
            <ChevronUp className={cn("w-3.5 h-3.5", isRefreshing && "animate-spin")} />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => refresh()} disabled={isLoading}>
            <RefreshCw className={cn("w-3.5 h-3.5", isRefreshing && "animate-spin")} />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreVertical className="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="gap-2 text-xs">
                <Download className="w-3.5 h-3.5" /> 导出文件列表
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2 text-xs text-destructive">
                <Trash2 className="w-3.5 h-3.5" /> 批量删除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* 搜索栏 */}
      <div className="p-2 border-b flex gap-2 relative">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="搜索当前目录..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (searchQuery) {
                  performSearch(side, searchQuery);
                } else {
                  refreshSide(side);
                }
              }
            }}
            disabled={isLoading}
            className="pl-8 pr-8 h-8 text-xs"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0.5 top-0.5 h-7 w-7"
              disabled={isLoading}
              onClick={() => performSearch(side, searchQuery)}
              title="搜索"
            >
              <Search className={cn("w-3.5 h-3.5", isSearching && "animate-pulse")} />
            </Button>
          )}
        </div>
        {/* 预设提示 */}
        {presetHint && (
          <div className="absolute right-12 bottom-10 text-[10px] text-primary bg-primary/10 px-2 py-1 rounded-md animate-in fade-in z-10">
            {presetHint}
          </div>
        )}
        {/* 快速搜索预设 */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" title="快速搜索预设">
              <Filter className="w-3.5 h-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuItem
              className="text-xs"
              onClick={() => {
                setSearchQuery(`*${todayDateNoDash}*`);
                setPresetHint("请点击搜索按钮查找今日文件");
                setTimeout(() => setPresetHint(""), 3000);
              }}
            >
              今日文件 (*{todayDateNoDash}*)
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-xs"
              onClick={() => {
                setSearchQuery(`*${todayDate}*`);
                setPresetHint("请点击搜索按钮查找今日文件");
                setTimeout(() => setPresetHint(""), 3000);
              }}
            >
              今日文件 (*{todayDate}*)
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-xs"
              onClick={() => {
                setSearchQuery(`*${todayDateNoDash}*, *${todayDate}*sport*, *${todayDate}*library* ,*_data_tmp_*_log.tar.gz, *wearablelog*`);
                setPresetHint("请点击搜索按钮查找运动健康日志");
                setTimeout(() => setPresetHint(""), 3000);
              }}
            >
              运动健康日志 (综合)
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-xs"
              onClick={() => {
                setSearchQuery(`*${todayDate}*sport*`);
                setPresetHint("请点击搜索按钮查找运动数据");
                setTimeout(() => setPresetHint(""), 3000);
              }}
            >
              运动数据 (*{todayDate}*sport*)
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-xs"
              onClick={() => {
                setSearchQuery(`*wearablelog*`);
                setPresetHint("请点击搜索按钮查找穿戴日志");
                setTimeout(() => setPresetHint(""), 3000);
              }}
            >
              穿戴日志 (*wearablelog*)
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-xs"
              onClick={() => {
                setSearchQuery(`*${todayDateUnderscore}_*-location.csv, *${todayDateUnderscore}_*-nmea.csv`);
                setPresetHint(`请点击搜索按钮查找今日 (${todayDate}) 的 location 和 nmea 文件`);
                setTimeout(() => setPresetHint(""), 3000);
              }}
            >
              今日 GPS 文件 (location/nmea)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="flex border rounded-md overflow-hidden shrink-0">
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-8 w-8 rounded-none border-none"
            onClick={() => setViewMode('list')}
            disabled={isLoading}
          >
            <LayoutList className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-8 w-8 rounded-none border-none"
            onClick={() => setViewMode('grid')}
            disabled={isLoading}
          >
            <LayoutGrid className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      
      {/* 文件列表表头 */}
      <div className="px-4 py-1 text-[11px] text-muted-foreground border-b flex items-center gap-3 bg-muted/10 uppercase font-bold tracking-wider">
        <Checkbox
          checked={filteredFiles.length > 0 && selectedPaths.length === filteredFiles.length}
          onCheckedChange={handleSelectAll}
        />
        <span
          className="flex-1 cursor-pointer hover:text-foreground transition-colors flex items-center gap-1"
          onClick={() => handleSort('name')}
        >
          名称 {sideState.sortField === 'name' && (sideState.sortOrder === 'asc' ? '↑' : '↓')}
        </span>
        {viewMode === 'list' && (
          <>
            <span
              className="w-16 text-right cursor-pointer hover:text-foreground transition-colors"
              onClick={() => handleSort('size')}
            >
              大小 {sideState.sortField === 'size' && (sideState.sortOrder === 'asc' ? '↑' : '↓')}
            </span>
            <span
              className="w-24 text-right cursor-pointer hover:text-foreground transition-colors"
              onClick={() => handleSort('modifiedTime')}
            >
              日期 {sideState.sortField === 'modifiedTime' && (sideState.sortOrder === 'asc' ? '↑' : '↓')}
            </span>
          </>
        )}
      </div>

      {/* 文件项容器 */}
      <div className="flex-1 min-h-0">
        {isSearching ? (
          <div className="h-full flex flex-col items-center justify-center text-xs text-muted-foreground gap-3">
            <div className="flex flex-col items-center gap-2">
              <div className="relative">
                <Search className="w-12 h-12 opacity-40 animate-pulse" />
                <div className="absolute inset-0 border-4 border-primary/20 rounded-full animate-[spin_1s_linear_infinite]" />
              </div>
              <p className="font-medium">正在搜索文件...</p>
              <Progress value={66} className="w-32 h-1" />
            </div>
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-xs text-muted-foreground gap-3 px-4">
            {!path ? (
              <>
                <FolderOpen className="w-10 h-10 opacity-20" />
                <p>请先选择路径</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="text-xs gap-1" onClick={openLocalFolder}>
                    <Monitor className="w-3 h-3" /> 打开本地文件夹
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs gap-1" onClick={openDeviceFolder}>
                    <Smartphone className="w-3 h-3" /> 打开手机目录
                  </Button>
                </div>
              </>
            ) : searchQuery ? (
              <>
                <Search className="w-8 h-8 opacity-20" />
                <p>未找到匹配 "{searchQuery}" 的文件</p>
                <Button variant="link" size="sm" className="text-xs" onClick={() => { setSearchQuery(''); refreshSide(side); }}>
                  清除搜索
                </Button>
              </>
            ) : (
              <>
                <Folder className="w-8 h-8 opacity-20" />
                <p>空目录</p>
              </>
            )}
          </div>
        ) : viewMode === 'list' ? (
          <Virtuoso
            style={{ height: '100%' }}
            data={filteredFiles}
            itemContent={(_index, file) => (
              <div className="px-2 py-0.5">
                <FileItem
                  file={file}
                  isSelected={selectedPaths.includes(file.path)}
                  onSelect={() => toggleSelect(side, file.path)}
                  onSelectExtended={(e) => {
                    toggleSelect(side, file.path, e.ctrlKey || e.metaKey, e.shiftKey);
                  }}
                  onDoubleClick={() => handleDoubleClick(file)}
                  onDragStart={handleDragStart}
                  onFolderDragOver={handleFolderDragOver}
                  onFolderDragLeave={handleFolderDragLeave}
                  onFolderDrop={handleFolderDrop}
                  isDropTarget={dropTargetFolder === file.path}
                  formatSize={formatSize}
                  formatDate={formatDate}
                  viewMode={viewMode}
                />
              </div>
            )}
          />
        ) : (
          <ScrollArea className="h-full">
            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {filteredFiles.map(file => (
                <FileItem
                  key={file.path}
                  file={file}
                  isSelected={selectedPaths.includes(file.path)}
                  onSelect={() => toggleSelect(side, file.path)}
                  onSelectExtended={(e) => {
                    toggleSelect(side, file.path, e.ctrlKey || e.metaKey, e.shiftKey);
                  }}
                  onDoubleClick={() => handleDoubleClick(file)}
                  onDragStart={handleDragStart}
                  onFolderDragOver={handleFolderDragOver}
                  onFolderDragLeave={handleFolderDragLeave}
                  onFolderDrop={handleFolderDrop}
                  isDropTarget={dropTargetFolder === file.path}
                  formatSize={formatSize}
                  formatDate={formatDate}
                  viewMode={viewMode}
                />
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
      
      {/* 面板底部 */}
      <div className="h-7 border-t flex items-center justify-between px-3 text-[10px] text-muted-foreground bg-muted/30">
        <div className="flex items-center gap-2">
          <span>已选: <span className="text-foreground font-medium">{selectedPaths.length}</span>{selectedTotalSize > 0 && <span className="text-muted-foreground"> ({formatSize(selectedTotalSize)})</span>}</span>
          <span className="opacity-50">/</span>
          <span>共 {files.length} 项</span>
        </div>
        {selectedPaths.length > 0 && (
          <div className="flex items-center gap-2">
            <Button
              variant="link"
              className="h-auto p-0 text-[10px] text-destructive gap-1"
              onClick={() => removeSelectedFromList(side)}
              title="从列表中移除选中项（不删除文件）"
            >
              <ListX className="w-3 h-3" />
              移除
            </Button>
            <Button
              variant="link"
              className="h-auto p-0 text-[10px] text-primary"
              onClick={() => clearSelection(side)}
            >
              清除选择
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

interface FileItemProps {
  file: FileEntry;
  isSelected: boolean;
  isDropTarget?: boolean;
  onSelect: () => void;
  onDoubleClick: () => void;
  formatSize: (bytes: number) => string;
  formatDate: (date: Date) => string;
  viewMode: 'list' | 'grid';
  onDragStart?: (e: DragEvent<HTMLDivElement>, file: FileEntry) => void;
  onFolderDragOver?: (e: DragEvent<HTMLDivElement>, folder: FileEntry) => void;
  onFolderDragLeave?: (e: DragEvent<HTMLDivElement>) => void;
  onFolderDrop?: (e: DragEvent<HTMLDivElement>, folder: FileEntry) => void;
}

const FileItem: React.FC<FileItemProps & { onSelectExtended: (e: React.MouseEvent) => void }> = ({
  file,
  isSelected,
  isDropTarget,
  onSelect,
  onSelectExtended,
  onDoubleClick,
  formatSize,
  formatDate,
  viewMode,
  onDragStart,
  onFolderDragOver,
  onFolderDragLeave,
  onFolderDrop
}) => {
  const isFolder = file.type === 'directory';

  if (viewMode === 'grid') {
    return (
      <div
        draggable
        onDragStart={(e) => onDragStart?.(e, file)}
        onDragOver={isFolder ? (e) => onFolderDragOver?.(e, file) : undefined}
        onDragLeave={isFolder ? onFolderDragLeave : undefined}
        onDrop={isFolder ? (e) => onFolderDrop?.(e, file) : undefined}
        className={cn(
          "flex flex-col items-center p-2 rounded-md cursor-pointer transition-all border border-transparent text-center group relative select-none",
          isSelected && "bg-primary/10 border-primary/20 shadow-sm",
          isDropTarget && "bg-green-500/20 border-green-500 ring-2 ring-green-500/50",
          !isSelected && !isDropTarget && "hover:bg-muted/50"
        )}
        onClick={onSelectExtended}
        onDoubleClick={onDoubleClick}
      >
        <Checkbox
          checked={isSelected}
          className={cn(
            "absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity",
            isSelected && "opacity-100"
          )}
          onCheckedChange={() => onSelect()}
          onClick={(e) => e.stopPropagation()}
        />
        <div className="mb-1">
          {isFolder ? (
            <Folder className="w-10 h-10 text-yellow-500 fill-yellow-500/10 group-hover:scale-110 transition-transform" />
          ) : (
            <FileIcon className="w-10 h-10 text-blue-500 opacity-80 group-hover:scale-110 transition-transform" />
          )}
        </div>
        <span className="text-xs truncate w-full px-1">{file.name}</span>
      </div>
    );
  }

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart?.(e, file)}
      onDragOver={isFolder ? (e) => onFolderDragOver?.(e, file) : undefined}
      onDragLeave={isFolder ? onFolderDragLeave : undefined}
      onDrop={isFolder ? (e) => onFolderDrop?.(e, file) : undefined}
      className={cn(
        "flex items-center gap-3 px-2 py-1 rounded-md cursor-pointer transition-all border-l-2 border-transparent select-none",
        "hover:bg-muted/50",
        isSelected && "bg-primary/10 border-primary",
        isDropTarget && "bg-green-500/20 border-green-500 ring-2 ring-green-500/50"
      )}
      onClick={onSelectExtended}
      onDoubleClick={onDoubleClick}
    >
      <Checkbox
        checked={isSelected}
        onCheckedChange={() => onSelect()}
        onClick={(e) => e.stopPropagation()}
      />
      <span className="flex-1 flex items-center gap-2 min-w-0">
        {isFolder ? (
          <Folder className="w-4 h-4 text-yellow-500 fill-yellow-500/20 shrink-0" />
        ) : (
          <FileIcon className="w-4 h-4 text-blue-500 shrink-0 opacity-80" />
        )}
        <span className="truncate text-xs">{file.name}</span>
      </span>
      <span className="w-16 text-right text-[10px] text-muted-foreground tabular-nums">
        {isFolder ? '--' : formatSize(file.size)}
      </span>
      <span className="w-24 text-right text-[10px] text-muted-foreground tabular-nums">
        {formatDate(file.modifiedTime)}
      </span>
    </div>
  );
};
